import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import api, { addCustomBrand, uploadAvatar } from '../../services/api';
import toast from 'react-hot-toast';
import { getAvatarUrl } from '../../utils/imageOptimization';

interface LoyaltyStats {
  totalPoints: number;
  loyaltyLevel: string;
  pointsToNextLevel: number;
}

interface Garage {
  bikeBrand?: string;
  bikeModel?: string;
  wheels?: string;
  helmet?: string;
  glasses?: string;
  shoes?: string;
  jersey?: string;
}

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [loyaltyStats, setLoyaltyStats] = useState<LoyaltyStats | null>(null);
  const [garage, setGarage] = useState<Garage | null>(null);
  const [garageBrands, setGarageBrands] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'profile' | 'garage' | 'loyalty'>('profile');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        birthDate: user.birthDate || '',
      });
    }
  }, [user]);
  const [garageData, setGarageData] = useState<Garage>({
    bikeBrand: '',
    bikeModel: '',
    wheels: '',
    helmet: '',
    glasses: '',
    shoes: '',
    jersey: '',
  });
  const [pickerVisible, setPickerVisible] = useState(false);
  const [pickerType, setPickerType] = useState<'bike' | 'wheels' | 'helmet' | 'glasses' | 'shoes' | 'jersey' | null>(null);
  const [customBrandModalVisible, setCustomBrandModalVisible] = useState(false);
  const [customBrandName, setCustomBrandName] = useState('');
  const [savingBrand, setSavingBrand] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchProfileData();
  }, []);

  useEffect(() => {
    if (garage) {
      setGarageData(garage);
    }
  }, [garage]);

  const fetchProfileData = async () => {
    try {
      const [loyaltyResponse, garageResponse, brandsResponse] = await Promise.all([
        api.get('/users/loyalty'),
        api.get('/garage'),
        api.get('/garage/brands'),
      ]);
      setLoyaltyStats(loyaltyResponse.data);
      setGarage(garageResponse.data.garage || {});
      setGarageBrands(brandsResponse.data);
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      await api.put('/users/profile', formData);
      await refreshUser();
      setEditing(false);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleGarageUpdate = async () => {
    try {
      await api.put('/garage', garageData);
      setGarage(garageData);
      toast.success(t('profile.garageUpdated'));
    } catch (error) {
      toast.error(t('profile.garageUpdateError'));
    }
  };

  const openPicker = (type: 'bike' | 'wheels' | 'helmet' | 'glasses' | 'shoes' | 'jersey') => {
    setPickerType(type);
    setPickerVisible(true);
  };

  const selectBrand = (brand: string) => {
    if (!pickerType) return;
    
    // If "Other" is selected, show custom brand modal
    if (brand === '__OTHER__') {
      setPickerVisible(false);
      setCustomBrandModalVisible(true);
      return;
    }
    
    setGarageData(prev => ({
      ...prev,
      [pickerType]: brand || null,
    }));
    
    setPickerVisible(false);
    setPickerType(null);
  };

  const handleAddCustomBrand = async () => {
    if (!customBrandName.trim() || !pickerType) {
      toast.error(t('profile.brandNameRequired'));
      return;
    }

    try {
      setSavingBrand(true);
      await addCustomBrand(customBrandName.trim(), pickerType);
      
      // Refresh brands list
      const brandsResponse = await api.get('/garage/brands');
      setGarageBrands(brandsResponse.data);
      
      // Set the new brand as selected
      setGarageData(prev => ({
        ...prev,
        [pickerType]: customBrandName.trim(),
      }));
      
      // Clear and close
      setCustomBrandName('');
      setCustomBrandModalVisible(false);
      
      toast.success(t('profile.brandAddedSuccess'));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('profile.brandAddError'));
    } finally {
      setSavingBrand(false);
    }
  };

  const getPickerOptions = () => {
    if (!garageBrands || !pickerType) return [];
    
    const brands = garageBrands.brands || garageBrands;
    
    switch (pickerType) {
      case 'bike':
        return brands.bikes || [];
      case 'wheels':
        return (brands.wheels || []).map((name: string) => ({ name, multiplier: 0, bonus: null }));
      case 'helmet':
        return (brands.helmets || []).map((name: string) => ({ name, multiplier: 0, bonus: null }));
      case 'glasses':
        return (brands.glasses || []).map((name: string) => ({ name, multiplier: 0, bonus: null }));
      case 'shoes':
        return (brands.shoes || []).map((name: string) => ({ name, multiplier: 0, bonus: null }));
      case 'jersey':
        return (brands.jerseys || []).map((name: string) => ({ name, multiplier: 0, bonus: null }));
      default:
        return [];
    }
  };

  const getCurrentValue = () => {
    if (!pickerType) return '';
    return garageData[pickerType] || '';
  };

  const getBikeBrandBonus = () => {
    if (!garageData.bikeBrand || !garageBrands) return null;
    const brands = garageBrands.brands || garageBrands;
    const bike = brands.bikes?.find((b: any) => b.name === garageData.bikeBrand);
    return bike?.bonus;
  };

  const getCategoryLabel = (type: string) => {
    const labels: Record<string, string> = {
      bike: t('profile.bikeBrand'),
      wheels: t('profile.wheels'),
      helmet: t('profile.helmet'),
      glasses: t('profile.glasses'),
      shoes: t('profile.shoes'),
      jersey: t('profile.jersey'),
    };
    return labels[type] || type;
  };

  const getCategoryIcon = (type: string) => {
    switch (type) {
      case 'bike':
        return '🚴';
      case 'wheels':
        return '⚙️';
      case 'helmet':
        return '🪖';
      case 'glasses':
        return '👓';
      case 'shoes':
        return '👟';
      case 'jersey':
        return '👕';
      default:
        return '📦';
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('profile.invalidImageType'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('profile.imageTooLarge'));
      return;
    }

    try {
      setUploadingAvatar(true);
      const response = await uploadAvatar(file);
      await refreshUser();
      toast.success(t('profile.avatarUploadSuccess'));
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      toast.error(error.message || t('profile.avatarUploadError'));
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user?.email?.[0].toUpperCase() || 'U';
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.title')}</h1>

      <div className="flex space-x-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('profile')}
          className={`pb-2 px-4 ${
            activeTab === 'profile'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-500'
          }`}
        >
          {t('profile.title')}
        </button>
        <button
          onClick={() => setActiveTab('loyalty')}
          className={`pb-2 px-4 ${
            activeTab === 'loyalty'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-500'
          }`}
        >
          {t('profile.loyaltyLevel')}
        </button>
        <button
          onClick={() => setActiveTab('garage')}
          className={`pb-2 px-4 ${
            activeTab === 'garage'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-500'
          }`}
        >
          {t('profile.garage')}
        </button>
      </div>

      {activeTab === 'profile' && (
        <div className="space-y-6">
          {/* Profile Header Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-6 mb-6">
              <div className="relative">
                <button
                  type="button"
                  onClick={handleAvatarClick}
                  disabled={uploadingAvatar}
                  className="relative group"
                >
                  {user?.avatarUrl ? (
                    <img
                      src={getAvatarUrl(user.avatarUrl)}
                      alt={`${user.firstName} ${user.lastName}`}
                      className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                      loading="eager"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-primary-600 flex items-center justify-center text-white text-3xl font-bold">
                      {getInitials()}
                    </div>
                  )}
                  <div className="absolute bottom-0 right-0 w-7 h-7 bg-primary-600 rounded-full flex items-center justify-center border-2 border-white">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  {uploadingAvatar && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    </div>
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : 'User'}
                </h2>
                <p className="text-gray-600 mb-2">{user?.email}</p>
                {user?.loyaltyNumber && (
                  <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 text-sm font-medium rounded-full">
                    {user.loyaltyNumber}
                  </span>
                )}
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Link
                to="/rides/my-rides"
                className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 transition-colors"
              >
                <div className="text-3xl mb-2">🚴</div>
                <p className="text-2xl font-bold text-primary-600 mb-1">{user?.totalRides || 0}</p>
                <p className="text-sm text-gray-600">{t('profile.ridesJoined')}</p>
              </Link>
              <Link
                to="/profile/loyalty"
                className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 transition-colors"
              >
                <div className="text-3xl mb-2">🏆</div>
                <p className="text-2xl font-bold text-primary-600 mb-1">
                  {loyaltyStats?.totalPoints || 0}
                </p>
                <p className="text-sm text-gray-600">{t('profile.loyaltyPoints')}</p>
              </Link>
            </div>
          </div>

          {/* Edit Profile Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{t('profile.editProfile')}</h2>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="text-primary-600 hover:text-primary-700"
                >
                  {t('common.edit')}
                </button>
              )}
            </div>
          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('auth.firstName')}
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('auth.lastName')}
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('auth.birthDate')}
                </label>
                <input
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleProfileUpdate}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  {t('common.save')}
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      firstName: user?.firstName || '',
                      lastName: user?.lastName || '',
                      birthDate: user?.birthDate || '',
                    });
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p><span className="font-medium">{t('auth.firstName')}:</span> {user?.firstName}</p>
              <p><span className="font-medium">{t('auth.lastName')}:</span> {user?.lastName}</p>
              <p><span className="font-medium">{t('auth.email')}:</span> {user?.email}</p>
              {user?.loyaltyNumber && (
                <p><span className="font-medium">{t('profile.loyaltyNumber')}:</span> {user.loyaltyNumber}</p>
              )}
            </div>
          )}
        </div>
          </div>
      )}

      {activeTab === 'loyalty' && loyaltyStats && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">{t('profile.loyaltyLevel')}</h2>
          <div className="space-y-4">
            <div>
              <span className="text-sm font-medium text-gray-500">{t('profile.totalPoints')}</span>
              <p className="text-2xl font-bold text-primary-600">{loyaltyStats.totalPoints}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{t('profile.loyaltyLevel')}</span>
              <p className="text-xl font-semibold">{loyaltyStats.loyaltyLevel}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{t('profile.pointsToNext')}</span>
              <p className="text-lg">{loyaltyStats.pointsToNextLevel}</p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
            <Link
              to="/profile/loyalty"
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 mr-3"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              {t('profile.viewLoyaltyProgram')}
            </Link>
            <Link
              to="/profile/badges"
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              {t('profile.badges')}
            </Link>
          </div>
        </div>
      )}

      {activeTab === 'garage' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-6">
              {t('profile.garageDescription')}
            </p>

            {/* Bike Section */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <span className="text-2xl mr-3">🚴</span>
                <h3 className="text-lg font-semibold text-gray-900">{t('profile.bike')}</h3>
              </div>
              
              {garageBrands && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('profile.bikeBrand')}
                    </label>
                    <button
                      type="button"
                      onClick={() => openPicker('bike')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md bg-white text-left flex justify-between items-center hover:bg-gray-50"
                    >
                      <span className={garageData.bikeBrand ? 'text-gray-900' : 'text-gray-500'}>
                        {garageData.bikeBrand || t('profile.selectBrand')}
                      </span>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {getBikeBrandBonus() && (
                      <p className="mt-2 text-sm text-green-600">
                        🎉 {garageData.bikeBrand} {t('profile.earnsBonus')} {getBikeBrandBonus()} {t('profile.bonusPointsPerRide')}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('profile.bikeModel')} ({t('profile.optional')})
                    </label>
                    <input
                      type="text"
                      value={garageData.bikeModel || ''}
                      onChange={(e) => setGarageData({ ...garageData, bikeModel: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder={t('profile.bikeModelPlaceholder')}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Other Equipment Sections */}
            {['wheels', 'helmet', 'glasses', 'shoes', 'jersey'].map((item) => (
              <div key={item} className="mb-6 pb-6 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0">
                <div className="flex items-center mb-4">
                  <span className="text-2xl mr-3">{getCategoryIcon(item)}</span>
                  <h3 className="text-lg font-semibold text-gray-900">{t(`profile.${item}`)}</h3>
                </div>
                
                {garageBrands && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t(`profile.${item}Brand`)}
                    </label>
                    <button
                      type="button"
                      onClick={() => openPicker(item as any)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md bg-white text-left flex justify-between items-center hover:bg-gray-50"
                    >
                      <span className={garageData[item as keyof Garage] ? 'text-gray-900' : 'text-gray-500'}>
                        {garageData[item as keyof Garage] || t('profile.selectBrand')}
                      </span>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleGarageUpdate}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                {t('profile.saveChanges')}
              </button>
            </div>
          </div>

          {/* Brand Picker Modal */}
          {pickerVisible && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50" onClick={() => setPickerVisible(false)}>
              <div className="bg-white rounded-t-lg w-full max-w-md mx-auto max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('profile.selectBrand')} - {getCategoryLabel(pickerType || '')}
                  </h3>
                  <button
                    onClick={() => setPickerVisible(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="overflow-y-auto flex-1">
                  <button
                    onClick={() => selectBrand('')}
                    className={`w-full px-4 py-3 text-left border-b border-gray-100 hover:bg-gray-50 ${
                      getCurrentValue() === '' ? 'bg-primary-50 text-primary-600' : 'text-gray-900'
                    }`}
                  >
                    {t('profile.none')}
                  </button>
                  {getPickerOptions().map((brand: any) => (
                    <button
                      key={brand.name}
                      onClick={() => selectBrand(brand.name)}
                      className={`w-full px-4 py-3 text-left border-b border-gray-100 hover:bg-gray-50 flex justify-between items-center ${
                        getCurrentValue() === brand.name ? 'bg-primary-50 text-primary-600' : 'text-gray-900'
                      }`}
                    >
                      <span>
                        {brand.name} {brand.bonus && `(${brand.bonus})`}
                      </span>
                      {getCurrentValue() === brand.name && (
                        <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                  <button
                    onClick={() => selectBrand('__OTHER__')}
                    className="w-full px-4 py-3 text-left border-t-2 border-gray-200 hover:bg-gray-50 text-primary-600 font-medium"
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t('profile.other')}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Custom Brand Modal */}
          {customBrandModalVisible && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => {
              setCustomBrandModalVisible(false);
              setCustomBrandName('');
            }}>
              <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('profile.addCustomBrand')} - {getCategoryLabel(pickerType || '')}
                  </h3>
                  <button
                    onClick={() => {
                      setCustomBrandModalVisible(false);
                      setCustomBrandName('');
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('profile.brandName')}
                  </label>
                  <input
                    type="text"
                    value={customBrandName}
                    onChange={(e) => setCustomBrandName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder={t('profile.enterBrandName')}
                    autoFocus
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    {t('profile.customBrandInfo')}
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setCustomBrandModalVisible(false);
                      setCustomBrandName('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleAddCustomBrand}
                    disabled={savingBrand || !customBrandName.trim()}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {savingBrand ? t('common.loading') : t('profile.addAndSelect')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">{t('profile.quickActions')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/profile/badges"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <div className="text-3xl mr-4">🏅</div>
            <div>
              <h3 className="font-medium text-gray-900">{t('profile.badges')}</h3>
              <p className="text-sm text-gray-500">{t('profile.viewBadges')}</p>
            </div>
          </Link>
          <Link
            to="/profile/settings"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <div className="text-3xl mr-4">⚙️</div>
            <div>
              <h3 className="font-medium text-gray-900">{t('profile.settings')}</h3>
              <p className="text-sm text-gray-500">{t('profile.appSettings')}</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
